import React from "react";
import { NavLink } from "react-router-dom";
import { NAV_ITEMS } from "@/config/nav";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, X } from "lucide-react";
import { cn } from "@/lib/utils";

export default function Sidebar({ collapsed, mobileOpen, onCloseMobile }) {
  const { effectiveRole } = useAuth();
  const items = NAV_ITEMS.filter((i) => i.roles.includes(effectiveRole));

  const content = (
    <div className="flex h-full flex-col">
      <div className={cn("flex items-center gap-3 px-5 h-16 border-b border-border shrink-0", collapsed && "justify-center px-0")}>
        <div className="h-9 w-9 rounded-xl bg-primary flex items-center justify-center text-primary-foreground shrink-0">
          <Sparkles className="h-5 w-5" />
        </div>
        {!collapsed && (
          <div className="leading-tight">
            <p className="font-display font-extrabold text-sm tracking-tight">Youngness</p>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Command Center</p>
          </div>
        )}
        <button className="ml-auto lg:hidden text-muted-foreground" onClick={onCloseMobile} data-testid="sidebar-close-mobile">
          <X className="h-5 w-5" />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
        {items.map((item) => (
          <NavLink
            key={item.id}
            to={item.path}
            end={item.path === "/"}
            onClick={onCloseMobile}
            data-testid={`sidebar-nav-${item.id}`}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors duration-200 group relative",
                isActive
                  ? "bg-primary/10 text-primary dark:bg-primary/15"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground",
                collapsed && "justify-center px-0"
              )
            }
            title={collapsed ? item.name : undefined}
          >
            <item.icon className="h-[18px] w-[18px] shrink-0" />
            {!collapsed && <span className="truncate">{item.name}</span>}
          </NavLink>
        ))}
      </nav>

      {!collapsed && (
        <div className="p-4 border-t border-border">
          <p className="text-[10px] text-muted-foreground leading-relaxed">
            All modules live. Customize centers, departments, KPIs & widgets in Settings.
          </p>
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Desktop */}
      <aside
        className={cn(
          "hidden lg:flex flex-col fixed inset-y-0 left-0 z-30 bg-card border-r border-border transition-[width] duration-200",
          collapsed ? "w-20" : "w-64"
        )}
      >
        {content}
      </aside>

      {/* Mobile */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onCloseMobile} />
          <aside className="absolute inset-y-0 left-0 w-64 bg-card border-r border-border animate-in slide-in-from-left duration-200">
            {content}
          </aside>
        </div>
      )}
    </>
  );
}
